import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { AdminPage, EmptyState, SaveBar, SectionCard } from '../../../components/design-system/admin';
import { useWireAdminApi } from '../../../common/hooks/use-wire-admin-api.hook';
import { WireSettings } from '../../../common/models/billing.model';
import { InvoicePreview, HEX_COLOR, PDF_DEFAULT_ACCENT } from '../../../modules/superadmin/billing/InvoicePreview';
import { LocalizedTextField, SettingsField, asLangMap } from '../../../modules/superadmin/billing/settings-fields';

type NumberKey = 'graceDays' | 'initialExpiryDays' | 'renewLeadDays' | 'reminderLeadDays' | 'cardFeePercent' | 'annualDiscountPercent';

/** Renewal timing (wire billing). Label and help are ADMIN_BILLING.SETTINGS.FIELDS.<i18n>[_HELP]. */
const TIMING_FIELDS: { key: NumberKey; i18n: string }[] = [
  { key: 'graceDays', i18n: 'GRACE_DAYS' },
  { key: 'initialExpiryDays', i18n: 'INITIAL_EXPIRY_DAYS' },
  { key: 'renewLeadDays', i18n: 'RENEW_LEAD_DAYS' },
  { key: 'reminderLeadDays', i18n: 'REMINDER_LEAD_DAYS' },
];
/** What vendors pay on top of or off the plan price. */
const PRICING_FIELDS: { key: NumberKey; i18n: string }[] = [
  { key: 'cardFeePercent', i18n: 'CARD_FEE_PERCENT' },
  { key: 'annualDiscountPercent', i18n: 'ANNUAL_DISCOUNT_PERCENT' },
];

const BANK_FIELDS: { key: string; i18n: string }[] = [
  { key: 'accountName', i18n: 'ACCOUNT_NAME' },
  { key: 'bankName', i18n: 'BANK_NAME' },
  { key: 'accountNumber', i18n: 'ACCOUNT_NUMBER' },
  { key: 'iban', i18n: 'IBAN' },
  { key: 'swift', i18n: 'SWIFT' },
  { key: 'routingNumber', i18n: 'ROUTING_NUMBER' },
  { key: 'address', i18n: 'ADDRESS' },
];

/**
 * Super-admin global wire-billing settings: the autoBilling toggle, timing knobs,
 * card fee and annual discount, the company bank details, and the seller
 * branding shown on invoice PDFs.
 */
export const WireSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const api = useWireAdminApi();

  const [settings, setSettings] = useState<WireSettings | null>(null);
  // The settings as last loaded or saved, to tell the admin what is unsaved.
  const [baseline, setBaseline] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  // Which language the footer + payment-instructions editors are editing.
  const [contentLang, setContentLang] = useState('en');

  const load = useCallback(() => {
    let active = true;
    setLoadFailed(false);
    api.getSettings()
      .then((s) => {
        if (!active) return;
        setSettings(s);
        setBaseline(JSON.stringify(s));
      })
      .catch(() => {
        if (!active) return;
        setLoadFailed(true);
        toast.error(t('ADMIN_BILLING.SETTINGS.LOAD_FAILED'));
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => load(), [load]);

  const patch = (p: Partial<WireSettings>) => setSettings((s) => (s ? { ...s, ...p } : s));
  const patchBank = (key: string, value: string) =>
    setSettings((s) => (s ? { ...s, bankDetails: { ...s.bankDetails, [key]: value } } : s));
  const patchBrand = (key: string, value: string) =>
    setSettings((s) => (s ? { ...s, branding: { ...(s.branding || {}), [key]: value } } : s));
  // Per-language free-text setters (footer + payment instructions).
  const patchInstructions = (lang: string, value: string) =>
    setSettings((s) => (s ? { ...s, bankDetails: { ...s.bankDetails, instructions: { ...asLangMap(s.bankDetails?.instructions), [lang]: value } } } : s));
  const patchFooter = (lang: string, value: string) =>
    setSettings((s) => (s ? { ...s, branding: { ...(s.branding || {}), footerText: { ...asLangMap(s.branding?.footerText), [lang]: value } } } : s));

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const updated = await api.updateSettings({
        autoBilling: settings.autoBilling,
        graceDays: Number(settings.graceDays),
        initialExpiryDays: Number(settings.initialExpiryDays),
        renewLeadDays: Number(settings.renewLeadDays),
        reminderLeadDays: Number(settings.reminderLeadDays),
        cardFeePercent: Number(settings.cardFeePercent),
        annualDiscountPercent: Number(settings.annualDiscountPercent),
        bankDetails: settings.bankDetails,
        branding: settings.branding,
      });
      setSettings(updated);
      setBaseline(JSON.stringify(updated));
      toast.success(t('ADMIN_BILLING.SETTINGS.SAVED'));
    } catch {
      toast.error(t('ADMIN_BILLING.SETTINGS.SAVE_FAILED'));
    } finally {
      setSaving(false);
    }
  };

  const numberField = (f: { key: NumberKey; i18n: string }) => (
    <SettingsField
      key={f.key}
      id={`wire-${f.key}`}
      label={t(`ADMIN_BILLING.SETTINGS.FIELDS.${f.i18n}`)}
      help={t(`ADMIN_BILLING.SETTINGS.FIELDS.${f.i18n}_HELP`)}
    >
      <Input
        id={`wire-${f.key}`}
        type="number"
        className="tabular-nums"
        value={(settings?.[f.key] as number) ?? 0}
        onChange={(e) => patch({ [f.key]: e.target.value } as any)}
      />
    </SettingsField>
  );

  const dirty = !!settings && JSON.stringify(settings) !== baseline;
  const accentValid = HEX_COLOR.test(settings?.branding?.accentColor || '');

  return (
    <AdminPage
      title={t('ADMIN_SHELL.NAV.BILLING_SETTINGS')}
      parents={[{ label: t('ADMIN_SHELL.NAV.SECTION_BILLING') }]}
      description={t('ADMIN_BILLING.SETTINGS.DESCRIPTION')}
    >
      {!settings ? (
        loadFailed ? (
          <Card className="py-0">
            <EmptyState
              title={t('ADMIN_SHELL.COMMON.LOAD_FAILED')}
              action={<Button variant="outline" onClick={load}>{t('ADMIN_SHELL.COMMON.RETRY')}</Button>}
              className="py-16"
            />
          </Card>
        ) : (
          <div className="flex flex-col gap-4" aria-busy="true" aria-label={t('ADMIN_SHELL.COMMON.LOADING')}>
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-56 rounded-xl" />)}
          </div>
        )
      ) : (
        <>
          {/* Automation */}
          <SectionCard
            title={t('ADMIN_BILLING.SETTINGS.AUTOMATION.TITLE')}
            description={t('ADMIN_BILLING.SETTINGS.AUTOMATION.DESCRIPTION')}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <Label htmlFor="wire-autoBilling" className="text-sm font-medium">
                  {t('ADMIN_BILLING.SETTINGS.AUTOMATION.AUTO_BILLING')}
                </Label>
                <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                  {t('ADMIN_BILLING.SETTINGS.AUTOMATION.AUTO_BILLING_HELP')}
                </p>
              </div>
              <Switch
                id="wire-autoBilling"
                className="mt-0.5 shrink-0"
                checked={!!settings.autoBilling}
                onCheckedChange={(checked) => patch({ autoBilling: checked })}
              />
            </div>

            <Separator className="my-5" />
            <p className="mb-4 text-sm font-medium">{t('ADMIN_BILLING.SETTINGS.AUTOMATION.TIMING')}</p>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">{TIMING_FIELDS.map(numberField)}</div>

            <Separator className="my-5" />
            <p className="mb-4 text-sm font-medium">{t('ADMIN_BILLING.SETTINGS.AUTOMATION.PRICING')}</p>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">{PRICING_FIELDS.map(numberField)}</div>
          </SectionCard>

          {/* Bank details */}
          <SectionCard
            title={t('ADMIN_BILLING.SETTINGS.BANK.TITLE')}
            description={t('ADMIN_BILLING.SETTINGS.BANK.DESCRIPTION')}
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {BANK_FIELDS.map((f) => (
                <SettingsField key={f.key} id={`wire-bank-${f.key}`} label={t(`ADMIN_BILLING.SETTINGS.BANK.${f.i18n}`)}>
                  <Input
                    id={`wire-bank-${f.key}`}
                    value={(settings.bankDetails as any)?.[f.key] || ''}
                    onChange={(e) => patchBank(f.key, e.target.value)}
                  />
                </SettingsField>
              ))}
            </div>
            <div className="mt-5">
              <LocalizedTextField
                id="wire-bank-instructions"
                label={t('ADMIN_BILLING.SETTINGS.BANK.INSTRUCTIONS')}
                value={settings.bankDetails?.instructions}
                lang={contentLang}
                onLangChange={setContentLang}
                onChange={patchInstructions}
                langLabel={t('ADMIN_BILLING.SETTINGS.CONTENT_LANGUAGE')}
              />
            </div>
          </SectionCard>

          {/* Branding */}
          <SectionCard
            title={t('ADMIN_BILLING.SETTINGS.BRANDING.TITLE')}
            description={t('ADMIN_BILLING.SETTINGS.BRANDING.DESCRIPTION')}
          >
            <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
              {/* Branding form */}
              <div className="flex flex-col gap-5">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <SettingsField id="wire-brand-taxId" label={t('ADMIN_BILLING.SETTINGS.BRANDING.TAX_ID')}>
                    <Input
                      id="wire-brand-taxId"
                      value={settings.branding?.taxId || ''}
                      onChange={(e) => patchBrand('taxId', e.target.value)}
                    />
                  </SettingsField>
                  <SettingsField id="wire-brand-registrationNumber" label={t('ADMIN_BILLING.SETTINGS.BRANDING.REGISTRATION_NUMBER')}>
                    <Input
                      id="wire-brand-registrationNumber"
                      value={settings.branding?.registrationNumber || ''}
                      onChange={(e) => patchBrand('registrationNumber', e.target.value)}
                    />
                  </SettingsField>
                  <SettingsField
                    id="wire-brand-accentColor"
                    label={t('ADMIN_BILLING.SETTINGS.BRANDING.ACCENT_COLOR')}
                    help={t('ADMIN_BILLING.SETTINGS.BRANDING.ACCENT_HELP')}
                  >
                    <div className="relative">
                      <Input
                        id="wire-brand-accentColor"
                        className="pe-9"
                        placeholder={PDF_DEFAULT_ACCENT}
                        value={settings.branding?.accentColor || ''}
                        onChange={(e) => patchBrand('accentColor', e.target.value)}
                      />
                      {/* The accent the PDF will use — PDF data, not app chrome. */}
                      <span
                        aria-hidden
                        className="absolute end-2 top-1/2 size-5 -translate-y-1/2 rounded-sm ring-1 ring-foreground/10"
                        style={{ backgroundColor: accentValid ? settings.branding!.accentColor : PDF_DEFAULT_ACCENT }}
                      />
                    </div>
                  </SettingsField>
                </div>
                <SettingsField id="wire-brand-companyAddress" label={t('ADMIN_BILLING.SETTINGS.BRANDING.COMPANY_ADDRESS')}>
                  <Textarea
                    id="wire-brand-companyAddress"
                    rows={2}
                    value={settings.branding?.companyAddress || ''}
                    onChange={(e) => patchBrand('companyAddress', e.target.value)}
                  />
                </SettingsField>
                <LocalizedTextField
                  id="wire-brand-footer"
                  label={t('ADMIN_BILLING.SETTINGS.BRANDING.FOOTER')}
                  value={settings.branding?.footerText}
                  lang={contentLang}
                  onLangChange={setContentLang}
                  onChange={patchFooter}
                  langLabel={t('ADMIN_BILLING.SETTINGS.CONTENT_LANGUAGE')}
                />
              </div>

              {/* Live preview (in the language being edited) */}
              <div>
                <div className="mb-2 flex items-baseline justify-between gap-2">
                  <p className="text-xs font-medium text-muted-foreground">{t('ADMIN_BILLING.SETTINGS.BRANDING.LIVE_PREVIEW')}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('ADMIN_BILLING.SETTINGS.BRANDING.PREVIEW_LANGUAGE', { lang: contentLang.toUpperCase() })}
                  </p>
                </div>
                <InvoicePreview branding={settings.branding} bankDetails={settings.bankDetails} lang={contentLang} />
              </div>
            </div>
          </SectionCard>

          <SaveBar dirty={dirty} saving={saving} onSave={save} saveLabel={t('ADMIN_BILLING.SETTINGS.SAVE')} testId="wire-settings-save" />
        </>
      )}
    </AdminPage>
  );
};

export default WireSettingsPage;
